import { useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  styled,
  Grid,
  Hidden,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import { motion } from "framer-motion";

import ProfessionalsCard from "./ProfessionalsCard";
import ReviewCard from "./ReviewCard";
import CustomerReviewInfoCard from "./CustomerReviewInfoCard";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import Prof1 from "../../assets/homepage/Professionals/prof1.png";
import Prof2 from "../../assets/homepage/Professionals/prof2.png";
import Prof3 from "../../assets/homepage/Professionals/prof3.png";
import Prof4 from "../../assets/newProfessionals/bg4.svg";

const SyletedNavBtn = styled(Button)(({ theme }) => ({
  width: "2.5rem",
  minWidth: "2.5rem",
  height: "2.5rem",
  padding: 0,
  margin: 0,
  borderRadius: "2.5rem",
  backgroundColor: "white",
  border: "1px solid",
  borderColor: theme.palette.alternate.main,
  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
}));

const professionals = [
  {
    profession: "Builders",
    photo_url: Prof2,
    info: "Join us on a journey where your dreams take shape. Our dedicated team of professionals is ready to guide you through the entire construction process, from conception to completion.",
    reviewCount: 25,
    customerCount: "2K+",
    ratingText: "Excellent",
    ratingValue: "4.8 / 5",
    totalReviews: 2530,
  },
  {
    profession: "Property Managers",
    photo_url: Prof3,
    info: "Your trusted guardians, ensure your properties are well-maintained, secure, and thriving. From tenant screenings to rent collection, they handle the nitty-gritty details, allowing you to sit back and relax.",
    reviewCount: 35,
    customerCount: "2K",
    ratingText: "Excellent",
    ratingValue: "4.9 / 5",
    totalReviews: 2000,
  },
  {
    profession: "Property Inspectors",
    photo_url: Prof4,
    info: "When it comes to choosing a Property Inspector, don't settle for anything less than excellence. Look for our qualified professionals who hold certifications, possess years of experience, and have a solid track record of client satisfaction. Your dream home deserves the utmost care and attention!",
    reviewCount: 20,
    ratingText: "Excellent",
    customerCount: "3K",
    ratingValue: "4.9 / 5",
    totalReviews: 3000,
  },
  {
    profession: "Home Improvements",
    photo_url: Prof1,
    info: "Are you dreaming of a stunning home makeover? Look no further! Our team of exceptional home improvement professionals is here to bring your vision to life. Be ready to experience the ultimate transformation and create a space that reflects your unique style and personality.",
    reviewCount: 40,
    customerCount: "1k",
    ratingText: "Good",
    ratingValue: "4.0 / 5",
    totalReviews: 1000,
  },
  {
    profession: "Builders",
    photo_url: Prof2,
    info: "Join us on a journey where your dreams take shape. Our dedicated team of professionals is ready to guide you through the entire construction process, from conception to completion.",
    reviewCount: 35,
    customerCount: "1.5K",
    ratingText: "Good",
    ratingValue: "4.0 / 5",
    totalReviews: 1500,
  },
  {
    profession: "Property Managers",
    photo_url: Prof3,
    info: "Your trusted guardians, ensure your properties are well-maintained, secure, and thriving. From tenant screenings to rent collection, they handle the nitty-gritty details, allowing you to sit back and relax.",
    reviewCount: 25,
    customerCount: "1.5K",
    ratingText: "Excellent",
    ratingValue: "4.9 / 5",
    totalReviews: 1500,
  },
  {
    profession: "Property Inspectors",
    photo_url: Prof4,
    info: "When it comes to choosing a Property Inspector, don't settle for anything less than excellence. Look for our qualified professionals who hold certifications, possess years of experience, and have a solid track record of client satisfaction. Your dream home deserves the utmost care and attention!",
    reviewCount: 15,
    ratingText: "Good",
    customerCount: "2.5K",
    ratingValue: "3.9 / 5",
    totalReviews: 2500,
  },
  {
    profession: "Home Improvements",
    photo_url: Prof1,
    info: "Are you dreaming of a stunning home makeover? Look no further! Our team of exceptional home improvement professionals is here to bring your vision to life. Be ready to experience the ultimate transformation and create a space that reflects your unique style and personality.",
    reviewCount: 45,
    customerCount: "4.5K",
    ratingText: "Excellent",
    ratingValue: "5.0 / 5",
    totalReviews: 4500,
  },
];

export default function ProfessionalsSection() {
  const theme = useTheme();
  // const xs = useMediaQuery(theme.breakpoints.down("sm"));
  const sm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const md = useMediaQuery(theme.breakpoints.up("md"));
  const swiperRef = useRef(null);

  const [currentSlide, setCurrentSlide] = useState(professionals[0]);

  const professionalsLink = `https://${
    process.env.REACT_APP_URL.includes("dev") ? "testing." : ""
  }industryprofessionals.com`;

  // Handler for when the slide changes
  const handleSlideChange = (swipeIndex) => {
    setCurrentSlide(professionals[swipeIndex]);
  };

  const handleNavigation = (type) => {
    if (swiperRef.current) {
      switch (type) {
        case "prev":
          swiperRef.current.slidePrev();
          break;
        case "next":
          swiperRef.current.slideNext();
          break;
        default:
          break;
      }
    }
  };

  const BrowseMore = () => (
    <a href={professionalsLink} target="_blank" rel="noreferrer">
      <Button
        variant="outlined"
        color="primary"
        size="small"
        disableElevation
        sx={{
          "&": { borderColor: "primary.main" },
          borderRadius: "50px",
          textTransform: "capitalize",
          padding: "12px 20px",
          height: "3rem",
          color: "text.orange.1",
          width: { xs: "100%", md: "auto" },
        }}
      >
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <Typography fontSize={"1rem"}>Browse more Professionals</Typography>
          <ArrowForwardRoundedIcon
            sx={{
              color: "text.orange.1",
            }}
          />
        </Stack>
      </Button>
    </a>
  );

  return (
    <Box sx={{ pb: 5 }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <Container
          maxWidth="xl"
          sx={{
            position: "relative",
            px: 2.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: { xs: 1, md: 1.25 },
            }}
          >
            <Stack gap={1}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight="600"
                fontSize={{ xs: "1.75rem", md: "2rem" }}
              >
                Need An Industry Professional?
              </Typography>
              <Typography
                variant="body1"
                fontSize={{ xs: "1rem:", sm: "18px" }}
                color={"neutral.dark"}
              >
                Attorney, Contractors, Stagers, Electricians, Plumbers, More!
              </Typography>
            </Stack>

            <Hidden mdDown>
              <BrowseMore />
            </Hidden>
          </Box>

          <Box
            sx={{
              position: "relative",
              "& .swiper-slide": {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              },
            }}
          >
            <Swiper
              modules={[Autoplay, Navigation]}
              centeredSlides
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              loop
              autoplay={{
                delay: 15000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              // slidesPerView="auto"
              // spaceBetween={sm ? "32" : "20"}
              breakpoints={{
                0: {
                  slidesPerView: 1.1,
                  spaceBetween: 12,
                  centeredSlidesBounds: true,
                },
                450: {
                  slidesPerView: 1.3,
                  spaceBetween: 20,
                  centeredSlidesBounds: true,
                },
                600: {
                  slidesPerView: 1.3,
                  spaceBetween: 30,
                  centeredSlidesBounds: true,
                },
                650: {
                  slidesPerView: 1.5,
                  spaceBetween: 30,
                  centeredSlidesBounds: true,
                },
                770: {
                  slidesPerView: 3,
                  spaceBetween: 10,
                },
                900: {
                  slidesPerView: 3,
                  spaceBetween: 20,
                },
                1100: {
                  slidesPerView: 3,
                  spaceBetween: 35,
                },
                1320: {
                  slidesPerView: 5,
                  spaceBetween: 16,
                },
              }}
              style={{
                padding: md ? "2rem 0" : sm ? "1.5rem 0" : "1.4rem 0",
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                {professionals?.map((item, index) => {
                  return (
                    <SwiperSlide
                      key={item.id}
                      style={{
                        width: "100%",
                        height: md ? "340px" : sm ? "330px" : "320px",
                      }}
                    >
                      {({ isActive }) => (
                        <motion.div
                          whileHover={{ y: isActive ? 0 : -5 }}
                          transition={{
                            type: isActive ? "none" : "spring",
                            stiffness: isActive ? 0 : 300,
                          }}
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            transition:
                              "height 0.25s ease, min-width 0.5s ease",
                            minWidth: isActive
                              ? md
                                ? "104%"
                                : "103%"
                              : "100%",
                            height: isActive
                              ? md
                                ? "360px"
                                : sm
                                ? "350px"
                                : "340px"
                              : "100%",
                          }}
                        >
                          <ProfessionalsCard
                            sliderIndex={index}
                            imageSrc={item.photo_url}
                            title={item.profession}
                            onClickButton={() => {}}
                            initialWidth="100%"
                            initialHeight="100%"
                            baseUrl={professionalsLink}
                            active={isActive}
                            handleSlideChange={handleSlideChange}
                          />
                        </motion.div>
                      )}
                    </SwiperSlide>
                  );
                })}
              </motion.div>
            </Swiper>

            <Stack gap={3} pt={3}>
              <Grid container spacing={"1.5rem"}>
                <Grid item xs={12} md={6}>
                  <ReviewCard
                    title={currentSlide.profession}
                    description={currentSlide.info}
                    reviewCount={currentSlide.review}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomerReviewInfoCard
                    title={currentSlide.profession}
                    customerCount={currentSlide.customerCount}
                    ratingText={currentSlide.ratingText}
                    ratingValue={currentSlide.ratingValue}
                    totalReviews={currentSlide.totalReviews}
                    avatarUrls={[
                      "https://mui.com/static/images/avatar/1.jpg",
                      "https://mui.com/static/images/avatar/2.jpg",
                      "https://mui.com/static/images/avatar/3.jpg",
                      "https://mui.com/static/images/avatar/4.jpg",
                      "https://mui.com/static/images/avatar/5.jpg",
                    ]}
                  />
                </Grid>
              </Grid>

              <Hidden mdUp>
                <BrowseMore />
              </Hidden>
              <Hidden mdDown>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                  gap={2.5}
                >
                  <SyletedNavBtn
                    color="alternate"
                    onClick={() => handleNavigation("prev")}
                  >
                    <ArrowBackOutlinedIcon color="alternate" />
                  </SyletedNavBtn>
                  <SyletedNavBtn
                    color="alternate"
                    onClick={() => handleNavigation("next")}
                  >
                    <ArrowForwardRoundedIcon color="alternate" />
                  </SyletedNavBtn>
                </Stack>
              </Hidden>
            </Stack>
          </Box>
        </Container>
      </motion.div>
    </Box>
  );
}
